import React, { useMemo } from 'react';
import { Table, TableProps, Button } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { SkillData, LevelTableData, CategoryTableData } from '../types';
import CategoriesTable from './CategoriesTable';

interface SkillStatisticsTableProps {
    chartData: SkillData[];
    studentId?: string;
}

const SkillStatisticsTable: React.FC<SkillStatisticsTableProps> = ({ chartData, studentId }) => {

    const dataSource: LevelTableData[] = useMemo(() => {
        return chartData.map(levelData => {
            let levelTotalActivity = 0;
            let levelTotalSuccess = 0;

            const categoryDetails: CategoryTableData[] = levelData.labels.map((categoryName, index) => {
                const totalActivity = levelData.totalActivities[index] || 0;
                const totalSuccess = levelData.totalSuccesses[index] || 0;
                const successScore = levelData.data[index] || 0;
                const topicDetails = levelData.topicDetails[categoryName] || [];

                levelTotalActivity += totalActivity;
                levelTotalSuccess += totalSuccess;

                return {
                    key: `${levelData.level}-${categoryName}`,
                    name: categoryName,
                    totalActivity: totalActivity,
                    totalSuccess: totalSuccess,
                    successScore: successScore,
                    topicDetails: topicDetails
                };
            }).sort((a, b) => b.totalActivity - a.totalActivity);

            const levelSuccessScore = levelTotalActivity > 0
                ? Math.round((levelTotalSuccess / levelTotalActivity) * 100)
                : 0;

            return {
                key: levelData.level,
                name: levelData.title,
                totalActivity: levelTotalActivity,
                totalSuccess: levelTotalSuccess,
                successScore: levelSuccessScore,
                categoryDetails: categoryDetails
            };
        }).sort((a, b) => a.name.localeCompare(b.name));
    }, [chartData]);

    const buildWorksheetData = (worksheet: ExcelJS.Worksheet, record: LevelTableData) => {
        worksheet.columns = [
            { key: 'col1', width: 46 }, // Name / Topic
            { key: 'col2', width: 18 }, // Total Activity
            { key: 'col3', width: 18 }, // Total Success
            { key: 'col4', width: 20 }  // Success Score (%)
        ];

        worksheet.mergeCells('A1:D2');
        const heroCell = worksheet.getCell('A1');
        heroCell.value = `SKILL PERFORMANCE & ANALYTICS REPORT\n${record.name}`;
        heroCell.font = { name: 'Segoe UI', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
        heroCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } };
        heroCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

        let currentRow = 4;

        worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
        const lvlHeader = worksheet.getCell(`A${currentRow}`);
        lvlHeader.value = '  LEVEL OVERVIEW SUMMARY';
        lvlHeader.font = { name: 'Segoe UI', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        lvlHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
        lvlHeader.alignment = { vertical: 'middle' };
        worksheet.getRow(currentRow).height = 26;
        currentRow++;

        const headers = ['Name', 'Total Activity', 'Total Success', 'Success Score (%)'];
        const thRow = worksheet.getRow(currentRow);
        headers.forEach((h, idx) => {
            const cell = thRow.getCell(idx + 1);
            cell.value = h;
            cell.font = { name: 'Segoe UI', size: 10, bold: true, color: { argb: 'FF334155' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { bottom: { style: 'medium', color: { argb: 'FFCBD5E1' } } };
        });
        worksheet.getRow(currentRow).height = 24;
        currentRow++;

        const dataRow = worksheet.getRow(currentRow);
        dataRow.getCell(1).value = record.name;
        dataRow.getCell(2).value = record.totalActivity;
        dataRow.getCell(3).value = record.totalSuccess;
        dataRow.getCell(4).value = record.successScore / 100;

        dataRow.getCell(1).font = { name: 'Segoe UI', size: 10, bold: true };
        dataRow.getCell(2).numFmt = '#,##0';
        dataRow.getCell(3).numFmt = '#,##0';
        dataRow.getCell(4).numFmt = '0.0%';

        worksheet.getRow(currentRow).height = 24;
        currentRow += 3;

        record.categoryDetails.forEach((cat) => {
            worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
            const catCell = worksheet.getCell(`A${currentRow}`);
            catCell.value = `  CATEGORY: ${cat.name}`;
            catCell.font = { name: 'Segoe UI', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
            catCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } };
            catCell.alignment = { vertical: 'middle' };
            worksheet.getRow(currentRow).height = 24;
            currentRow++;

            const topThRow = worksheet.getRow(currentRow);
            ['Topic Breakdown', 'Total Activity', 'Total Success', 'Success Score (%)'].forEach((h, idx) => {
                const cell = topThRow.getCell(idx + 1);
                cell.value = h;
                cell.font = { name: 'Segoe UI', size: 9, bold: true, color: { argb: 'FF475569' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
            currentRow++;

            if (cat.topicDetails && cat.topicDetails.length > 0) {
                cat.topicDetails.forEach((topic: any, idx: number) => {
                    const r = worksheet.getRow(currentRow);
                    const act = topic.totalActivity || topic.activityCount || 0;
                    const suc = topic.totalSuccess || topic.successCount || 0;
                    const score = act > 0 ? suc / act : 0;

                    r.getCell(1).value = `    ▪  ${topic.name || topic.topicName}`;
                    r.getCell(2).value = act;
                    r.getCell(3).value = suc;
                    r.getCell(4).value = score;

                    if (idx % 2 === 1) {
                        for (let c = 1; c <= 4; c++) {
                            r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
                        }
                    }

                    r.getCell(2).numFmt = '#,##0';
                    r.getCell(3).numFmt = '#,##0';
                    r.getCell(4).numFmt = '0.0%';

                    worksheet.getRow(currentRow).height = 20;
                    currentRow++;
                });
            }
            currentRow += 1;
        });
    };

    const exportRowToExcel = async (record: LevelTableData) => {
        const workbook = new ExcelJS.Workbook();
        const safeSheetName = record.name.replace(/[\\/?*[\]]/g, '').substring(0, 31);
        const worksheet = workbook.addWorksheet(safeSheetName || 'Skill Analytics', {
            views: [{ showGridLines: true }]
        });

        buildWorksheetData(worksheet, record);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${record.name.replace(/[^a-zA-Z0-9_-]/g, '_')}_Analytics.xlsx`);
    };

    const exportAllToExcel = async () => {
        if (dataSource.length === 0) return;

        const workbook = new ExcelJS.Workbook();

        dataSource.forEach((record, index) => {
            let safeSheetName = record.name.replace(/[\\/?*[\]]/g, '').substring(0, 31);
            if (!safeSheetName) safeSheetName = `Level_${index + 1}`;

            const worksheet = workbook.addWorksheet(safeSheetName, {
                views: [{ showGridLines: true }]
            });

            buildWorksheetData(worksheet, record);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `All_Levels_Skill_Analytics.xlsx`);
    };

    const columns: TableProps<LevelTableData>['columns'] = [
        { title: 'Level Name', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
        { title: 'Total Activity', dataIndex: 'totalActivity', key: 'totalActivity', sorter: (a, b) => a.totalActivity - b.totalActivity },
        { title: 'Total Success', dataIndex: 'totalSuccess', key: 'totalSuccess', sorter: (a, b) => a.totalSuccess - b.totalSuccess },
        {
            title: 'Success Score (%)',
            dataIndex: 'successScore',
            key: 'successScore',
            render: (score: number) => (
                <span className={`badge badge-sm ${score >= 70 ? 'bg-success' : score > 0 ? 'bg-warning' : 'bg-secondary'} d-inline-flex align-items-center`}>
                    {score}%
                </span>
            ),
            sorter: (a, b) => a.successScore - b.successScore,
        },
        {
            title: 'Export',
            key: 'export',
            render: (_, record: LevelTableData) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<FileExcelOutlined />}
                    className="btn-excel-export"
                    style={{ backgroundColor: '#217346', borderColor: '#217346' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        exportRowToExcel(record);
                    }}
                >
                    Export
                </Button>
            ),
        },
    ];

    const expandedRowRender = (record: LevelTableData) => {
        if (record.categoryDetails.length === 0) {
            return <p style={{ margin: 0, padding: '10px 40px' }}>No skill-level data found for this level.</p>;
        }
        return <CategoriesTable data={record.categoryDetails} studentId={studentId} />;
    };

    const CustomExpandIcon = ({ expanded, onExpand, record }: any) => (
        <span
            onClick={e => onExpand(record, e)}
            style={{
                cursor: 'pointer',
                fontSize: '14px',
                color: '#000',
                padding: '0 5px',
                transition: 'transform 0.3s ease-in-out',
                display: 'inline-block',
                transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)',
                lineHeight: 1
            }}
            className="fa fa-plus"
            aria-label={expanded ? "Collapse row" : "Expand row"}
        />
    );

    if (dataSource.length === 0) return null;

    return (
        <div className="container my-5">
            <style>
                {`
                .ant-table-row-expand-icon-cell {
                    width: 40px;
                    padding-left: 10px !important;
                    padding-right: 10px !important;
                }
                .ant-table-row-expand-icon {
                    background: none !important;
                    border: none !important;
                }
                .ant-table-wrapper {
                    overflow-x: auto;
                }
                .btn-excel-export:hover {
                    background-color: #1e6b40 !important;
                    border-color: #1e6b40 !important;
                }
                `}
            </style>

            <div className="row justify-content-center">
                <div className="col-12">
                    <div className="card-body">
                        <div className="d-flex justify-content-end mb-3">
                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                className="btn-excel-export"
                                style={{ backgroundColor: '#217346', borderColor: '#217346' }}
                                onClick={exportAllToExcel}
                            >
                                Export All to Excel
                            </Button>
                        </div>

                        <Table
                            dataSource={dataSource}
                            columns={columns}
                            pagination={{ pageSize: 5 }}
                            size="middle"
                            expandable={{
                                expandedRowRender,
                                expandIcon: CustomExpandIcon,
                                expandIconColumnIndex: 0,
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(SkillStatisticsTable);